import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import axios from 'axios';
import Loader from '../common/Loader';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 400,
    height: 'auto',
    bgcolor: 'background.paper',
    border: 'none',
    boxShadow: 24,
    borderRadius: "16px",
    p: { xs: 2, md: 2 },
};

const FormTextField = styled(TextField)({
    marginBottom: '16px',
});

const AddArticleModal = ({ open, handleOpen, handleClose, allArticles }) => {
    const [formData, setFormData] = useState({
        title: '',
        createdAt: '',
        author: '',
        description: ''
    });

    const [imageFiles, setImageFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const clearState = () => {
        setFormData({
            title: '',
            createdAt: '',
            author: '',
            description: ''
        });
        setImageFiles([]);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleDescriptionChange = (value) => {
        setFormData({ ...formData, description: value });
    };

    const handleImageChange = (e) => {
        setImageFiles([...e.target.files]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const imageUrls = await Promise.all(imageFiles.map(async (file) => {
            const uploadData = new FormData();
            uploadData.append('file', file);
            uploadData.append('upload_preset', process.env.REACT_APP_UPLOAD_PRESET);
            setIsLoading(true);
            try {
                const response = await axios.post(
                    `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUD_NAME}/image/upload`,
                    uploadData
                );
                return response.data.secure_url;
            } catch (error) {
                console.error('Error uploading image to Cloudinary:', error);
                return '';
            }
        }));

        const processedData = {
            ...formData,
            images: imageUrls,
            metaTitle: formData.metaTitle,
            metaDescription: formData.metaDescription,
            keywords: formData.keywords,
            canonical: formData.canonical,
            description: formData.description
        };

        handleAddBlog(processedData);
    };

    const handleAddBlog = (blogData) => {
        let data = JSON.stringify({
            "title": blogData?.title,
            "author": blogData?.author,
            "images": blogData?.images,
            "createdAt": new Date()?.toISOString()?.slice(0, 10),
            "metaTitle": blogData?.metaTitle,
            "metaDescription": blogData?.metaDescription,
            "keywords": blogData?.keywords,
            "canonical": blogData?.canonical,
            "description": blogData?.description
        });

        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${process.env.REACT_APP_API_URL}/articles/add-articles`,
            headers: { 'Content-Type': 'application/json' },
            data: data
        };

        setIsLoading(true);
        axios.request(config)
            .then(() => {
                setIsLoading(false);
                clearState();
                handleClose();
                allArticles();
            })
            .catch((error) => {
                console.log(error);
                setIsLoading(false);
            });
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={style}>
                <Typography variant="h6" component="h2" className='relative'>
                    Add New Article
                    <div onClick={handleClose} className='text-xl absolute top-0 right-2 cursor-pointer text-[#FF8607]'>x</div>
                </Typography>

                <form className='my-2 py-2 px-1 h-[22rem] overflow-x-hidden overflow-y-scroll' onSubmit={handleSubmit}>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <FormTextField
                                fullWidth
                                label="Title"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                required
                            />
                        </Grid>

<Grid item xs={12}>
    <FormTextField
        fullWidth
        label="Meta Title (SEO)"
        name="metaTitle"
        value={formData.metaTitle || ''}
        onChange={handleChange}
        placeholder="Article Title | BBSR Services"
    />
</Grid>

<Grid item xs={12}>
    <FormTextField
        fullWidth
        label="Meta Description (SEO)"
        name="metaDescription"
        value={formData.metaDescription || ''}
        onChange={handleChange}
        placeholder="Brief description for search engines"
        multiline
        rows={2}
    />
</Grid>

<Grid item xs={12}>
    <FormTextField
        fullWidth
        label="Keywords (SEO)"
        name="keywords"
        value={formData.keywords || ''}
        onChange={handleChange}
        placeholder="article, business, GST, company registration"
    />
</Grid>

<Grid item xs={12}>
    <FormTextField
        fullWidth
        label="Canonical URL (SEO)"
        name="canonical"
        value={formData.canonical || ''}
        onChange={handleChange}
        placeholder="https://www.bbsrservices.co.in/article/your-article"
    />
</Grid>
                        <Grid item xs={12}>
                            <FormTextField
                                fullWidth
                                label="Author"
                                name="author"
                                value={formData.author}
                                onChange={handleChange}
                                required
                            />
                        </Grid>

                        {/* React Quill for Description */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" className="mb-1">
                                Description
                            </Typography>
                            <ReactQuill
                                theme="snow"
                                value={formData.description}
                                onChange={handleDescriptionChange}
                                className="bg-white text-black"
                                modules={{
                                    toolbar: [
                                        [{ header: [1, 2, 3, false] }],
                                        ['bold', 'italic', 'underline', 'strike'],
                                        [{ color: [] }, { background: [] }],
                                        [{ align: [] }],
                                        ['blockquote', 'code-block'],
                                        ['link', 'image'],
                                        ['clean'],
                                    ],
                                }}
                                formats={[
                                    'header',
                                    'bold',
                                    'italic',
                                    'underline',
                                    'strike',
                                    'color',
                                    'background',
                                    'align',
                                    'blockquote',
                                    'code-block',
                                    'link',
                                    'image',
                                ]}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <div className='mb-2'>Choose Article Images (600×500 size)</div>
                            <input type="file" accept="image/*" onChange={handleImageChange} multiple required />
                        </Grid>

                        <Grid item xs={12}>
                            <button
                                type="submit"
                                className="w-full flex justify-center items-center bg-gradient-to-r from-[#FF3131] to-[#ff9301] text-white font-semibold tracking-wider py-2 px-4 rounded-md hover:bg-red-600 transition duration-300 ease-in-out"
                            >
                                {isLoading ? <Loader /> : 'Add Article'}
                            </button>
                        </Grid>
                    </Grid>
                </form>
            </Box>
        </Modal>
    );
};

export default AddArticleModal;
